import notifications from 'notifications';
import {getStore} from 'redux-stores/Store';
import * as LoadingScreen from 'ui-library/LoadingScreen';

import * as StageFlowNodeDAO from './dao/StageFlowNodeDAO';
import {
  StageConfiguration,
  StageFlowNode,
  StageRun,
  WorkflowTemplateVersionDetails,
} from './WorkflowConstants';

enum StageFlowNodeActionType {
  WORKFLOW_STAGE_FLOW_NODE_FETCH_COMPLETED = 'WORKFLOW_STAGE_FLOW_NODE_FETCH_COMPLETED',
  WORKFLOW_WORKFLOW_TEMPLATE_VERSION_DETAILS_FETCH_COMPLETED = 'WORKFLOW_WORKFLOW_TEMPLATE_VERSION_DETAILS_FETCH_COMPLETED',
  WORKFLOW_STAGE_CONFIGURATION_FETCH_COMPLETED = 'WORKFLOW_STAGE_CONFIGURATION_FETCH_COMPLETED',
}

interface StageFlowNodeFetchCompletedAction {
  type: StageFlowNodeActionType.WORKFLOW_STAGE_FLOW_NODE_FETCH_COMPLETED;
  stageFlowNode: StageFlowNode;
  stageRun?: StageRun | null;
}

export function editStageFlowNode(
  stageFlowNodeId: string,
  {
    parentIds,
    experimentalConditionValueIds,
  }: {parentIds?: Array<string>; experimentalConditionValueIds?: Array<string>}
): void {
  LoadingScreen.show();
  StageFlowNodeDAO.editStageFlowNode(stageFlowNodeId, {parentIds, experimentalConditionValueIds})
    .done(({stageFlowNode, stageRun}: {stageFlowNode: StageFlowNode; stageRun: StageRun | null}) => {
      getStore().dispatch({
        type: StageFlowNodeActionType.WORKFLOW_STAGE_FLOW_NODE_FETCH_COMPLETED,
        stageFlowNode,
        stageRun,
      });
    })
    .fail(notifications.notifyError)
    .always(() => {
      LoadingScreen.hide();
    });
}

interface WorkflowTemplateVersionDetailsFetchCompletedAction extends WorkflowTemplateVersionDetails {
  type: StageFlowNodeActionType.WORKFLOW_WORKFLOW_TEMPLATE_VERSION_DETAILS_FETCH_COMPLETED;
}

export function deleteStageFlowNode(
  stageFlowNodeId: string,
  {shouldDeleteDownstreamNodes}: {shouldDeleteDownstreamNodes: boolean}
): void {
  LoadingScreen.show();
  StageFlowNodeDAO.deleteStageFlowNode(stageFlowNodeId, {shouldDeleteDownstreamNodes})
    .done(
      ({workflowTemplateVersion, stageConfigurations, stageFlowNodes}: WorkflowTemplateVersionDetails) => {
        getStore().dispatch({
          type: StageFlowNodeActionType.WORKFLOW_WORKFLOW_TEMPLATE_VERSION_DETAILS_FETCH_COMPLETED,
          workflowTemplateVersion,
          stageConfigurations,
          stageFlowNodes,
        });
      }
    )
    .fail(notifications.notifyError)
    .always(() => {
      LoadingScreen.hide();
    });
}

interface StageConfigurationFetchCompletedAction {
  type: StageFlowNodeActionType.WORKFLOW_STAGE_CONFIGURATION_FETCH_COMPLETED;
  stageConfiguration: StageConfiguration;
}

export function createStageFlowNode({
  stageConfigurationId,
  parentIds,
}: {
  stageConfigurationId: string;
  parentIds: Array<string>;
}): void {
  LoadingScreen.show();
  StageFlowNodeDAO.createStageFlowNode({stageConfigurationId, parentIds})
    .done(
      ({
        stageConfiguration,
        stageFlowNode,
      }: {
        stageFlowNode: StageFlowNode;
        stageConfiguration: StageConfiguration;
      }) => {
        getStore().dispatch({
          type: StageFlowNodeActionType.WORKFLOW_STAGE_FLOW_NODE_FETCH_COMPLETED,
          stageFlowNode,
        });
        getStore().dispatch({
          type: StageFlowNodeActionType.WORKFLOW_STAGE_CONFIGURATION_FETCH_COMPLETED,
          stageConfiguration,
        });
      }
    )
    .fail(notifications.notifyError)
    .always(() => {
      LoadingScreen.hide();
    });
}

export function duplicateStageFlowNodeTree({stageFlowNodeId}: {stageFlowNodeId: string}): void {
  LoadingScreen.show();
  StageFlowNodeDAO.duplicateStageFlowNodeTree({stageFlowNodeId})
    .done(
      ({workflowTemplateVersion, stageConfigurations, stageFlowNodes}: WorkflowTemplateVersionDetails) => {
        getStore().dispatch({
          type: StageFlowNodeActionType.WORKFLOW_WORKFLOW_TEMPLATE_VERSION_DETAILS_FETCH_COMPLETED,
          workflowTemplateVersion,
          stageConfigurations,
          stageFlowNodes,
        });
      }
    )
    .fail(notifications.notifyError)
    .always(() => {
      LoadingScreen.hide();
    });
}

export function explodeStageFlowNodeTree({stageFlowNodeId}: {stageFlowNodeId: string}): void {
  LoadingScreen.show();
  StageFlowNodeDAO.explodeStageFlowNodeTree({stageFlowNodeId})
    .done(
      ({workflowTemplateVersion, stageConfigurations, stageFlowNodes}: WorkflowTemplateVersionDetails) => {
        getStore().dispatch({
          type: StageFlowNodeActionType.WORKFLOW_WORKFLOW_TEMPLATE_VERSION_DETAILS_FETCH_COMPLETED,
          workflowTemplateVersion,
          stageConfigurations,
          stageFlowNodes,
        });
      }
    )
    .fail(notifications.notifyError)
    .always(() => {
      LoadingScreen.hide();
    });
}

export type StageFlowNodeAction =
  | StageFlowNodeFetchCompletedAction
  | WorkflowTemplateVersionDetailsFetchCompletedAction
  | StageConfigurationFetchCompletedAction;
